import XLSX from 'xlsx-js-style';
import { getAmountFromCell } from './editableProfitAndLossUtils';
import { applyWorkbookRtl } from '../../../Utils/ExcelReport';

export const ROW_TYPES = {
  REPORT_TITLE: 'reportTitle',
  REPORT_META: 'reportMeta',
  SECTION_MAJOR: 'sectionMajor',
  SECTION_DETAIL: 'sectionDetail',
  COLUMN_HEADER: 'columnHeader',
  PAL_CODE_HEADER: 'palCodeHeader',
  ACCOUNT: 'account',
  PAL_SUBTOTAL: 'palSubtotal',
  CATEGORY_TOTAL: 'categoryTotal',
  GROUP_TOTAL: 'groupTotal',
  RUNNING_PL_TOTAL: 'runningPlTotal',
  GRAND_TOTAL: 'grandTotal',
  PROFIT_INDICATOR: 'profitIndicator',
};

const ACCOUNTING_NUM_FMT = '#,##0.00_);(#,##0.00)';

/** Visual language aligned with ProfitAndLossReportHtml / server PL Excel. */
const COLORS = {
  reportTitleBg: 'FFFFFF',
  reportTitleFg: '2F4BFF',
  reportMetaBg: 'FFFFFF',
  reportMetaFg: '1F2937',
  sectionMajorBg: 'FFFFFF',
  sectionMajorFg: '1E308F',
  sectionDetailBg: 'FFFFFF',
  sectionDetailFg: '1F2937',
  columnHeaderBg: '2F4BFF',
  columnHeaderFg: 'FFFFFF',
  palCodeBg: 'EFF2FF',
  accountBg: 'FFFFFF',
  accountAltBg: 'F6F8FF',
  palSubtotalBg: 'F6F8FF',
  categoryTotalBg: 'DDE4FF',
  groupTotalBg: 'D7DFFF',
  grandTotalBg: '1E308F',
  grandTotalFg: 'FFFFFF',
  border: 'C3CBDB',
  borderDark: '1E308F',
};

const ALIGN_RIGHT = { horizontal: 'right', vertical: 'center', wrapText: true };
const ALIGN_LEFT = { horizontal: 'right', vertical: 'center' };

const thinBorder = { style: 'thin', color: { rgb: COLORS.border } };
const thinBlack = { style: 'thin', color: { rgb: COLORS.borderDark } };
const doubleBlack = { style: 'double', color: { rgb: COLORS.borderDark } };

const cellFill = (rgb) => ({ fill: { fgColor: { rgb } } });

const withFill = (style, bg, fg) => {
  const next = { ...style, ...cellFill(bg) };
  if (fg) {
    next.font = { ...(next.font || {}), color: { rgb: fg } };
  }
  return next;
};

const titleStyle = withFill(
  { font: { bold: true, sz: 20 }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true } },
  COLORS.reportTitleBg,
  COLORS.reportTitleFg,
);
const metaStyle = withFill(
  { font: { bold: true, sz: 12 }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true } },
  COLORS.reportMetaBg,
  COLORS.reportMetaFg,
);
const sectionMajorStyle = withFill(
  { font: { bold: true, sz: 14 }, alignment: ALIGN_RIGHT },
  COLORS.sectionMajorBg,
  COLORS.sectionMajorFg,
);
const sectionDetailStyle = withFill(
  { font: { bold: true, sz: 12 }, alignment: ALIGN_RIGHT },
  COLORS.sectionDetailBg,
  COLORS.sectionDetailFg,
);
const columnHeaderStyle = withFill(
  {
    font: { bold: true, sz: 12 },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder },
  },
  COLORS.columnHeaderBg,
  COLORS.columnHeaderFg,
);
const palCodeHeaderStyle = withFill(
  {
    font: { bold: true, sz: 12 },
    alignment: ALIGN_RIGHT,
    border: { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder },
  },
  COLORS.palCodeBg,
  COLORS.sectionMajorFg,
);
const accountStyle = (alt) =>
  withFill(
    { font: { sz: 11 }, alignment: ALIGN_RIGHT, border: { bottom: thinBorder } },
    alt ? COLORS.accountAltBg : COLORS.accountBg,
  );
const boldStyle = (bg) =>
  withFill({ font: { bold: true, sz: 11 }, alignment: ALIGN_RIGHT }, bg || COLORS.palSubtotalBg);
const categoryTotalStyle = withFill(
  { font: { bold: true, sz: 11 }, alignment: ALIGN_RIGHT },
  COLORS.categoryTotalBg,
);
const groupTotalStyle = withFill(
  { font: { bold: true, sz: 11 }, alignment: ALIGN_RIGHT },
  COLORS.groupTotalBg,
);
const grandTotalStyle = withFill(
  {
    font: { bold: true, sz: 13 },
    alignment: ALIGN_RIGHT,
    border: { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder },
  },
  COLORS.grandTotalBg,
  COLORS.grandTotalFg,
);

const amountStyle = (bg) =>
  withFill({ font: { sz: 11 }, alignment: ALIGN_LEFT, border: { bottom: thinBorder } }, bg || COLORS.accountBg);
const boldAmountStyle = (bg) =>
  withFill(
    { font: { bold: true, sz: 11 }, alignment: ALIGN_LEFT, border: { bottom: thinBorder } },
    bg || COLORS.palSubtotalBg,
  );
const categoryAmountStyle = withFill(
  { font: { bold: true, sz: 11 }, alignment: ALIGN_LEFT, border: { bottom: doubleBlack } },
  COLORS.categoryTotalBg,
);
const groupAmountStyle = withFill(
  { font: { bold: true, sz: 11 }, alignment: ALIGN_LEFT, border: { bottom: doubleBlack } },
  COLORS.groupTotalBg,
);
const grandAmountStyle = withFill(
  {
    font: { bold: true, sz: 13 },
    alignment: ALIGN_LEFT,
    border: { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder },
  },
  COLORS.grandTotalBg,
  COLORS.grandTotalFg,
);

export const getPercentageFromCell = (cell) => {
  if (!cell) return null;
  const text = cell.textContent?.replace(/\s+/g, ' ').trim() || '';
  return text || null;
};

export const classifyTableRow = (tr) => {
  if (!tr || tr.tagName !== 'TR') return null;
  if (tr.querySelector('td.section-header-block')) return ROW_TYPES.SECTION_MAJOR;
  if (tr.querySelector('td.overflowed')) return ROW_TYPES.PAL_CODE_HEADER;
  if (tr.classList.contains('tr-final-row-palcode-Report')) return ROW_TYPES.GRAND_TOTAL;
  if (tr.classList.contains('pal-code-account-group-total-row')) return ROW_TYPES.GROUP_TOTAL;
  if (tr.classList.contains('ag-classification-total-row')) return ROW_TYPES.CATEGORY_TOTAL;
  if (tr.classList.contains('tr-final-row-palcode')) return ROW_TYPES.PAL_SUBTOTAL;
  if (tr.classList.contains('tr-final-row-palcode-category')) return ROW_TYPES.RUNNING_PL_TOTAL;
  if (tr.classList.contains('pl-profit-indicator-row')) return ROW_TYPES.PROFIT_INDICATOR;
  if (tr.querySelector('td.td-account-cell')) return ROW_TYPES.ACCOUNT;
  if (tr.classList.contains('hoverTr')) return ROW_TYPES.ACCOUNT;
  return null;
};

const isReportHeaderTheadRow = (tr) =>
  Boolean(tr?.querySelector('.report-header-cell, .report-page-header'));

const expandHeaderCells = (tr) => {
  const cells = [];
  tr.querySelectorAll('th, td').forEach((cell) => {
    if (cell.classList.contains('report-header-cell')) return;
    const text = normalizeHeaderText(cell.textContent);
    const colspan = parseInt(cell.getAttribute('colspan') || '1', 10);
    for (let i = 0; i < colspan; i += 1) {
      cells.push(i === 0 ? text : '');
    }
  });
  return cells;
};

const normalizeHeaderText = (text) => text?.replace(/\s+/g, ' ').trim() || '';

const padCells = (cells, columnCount) => {
  const padded = [...cells];
  while (padded.length < columnCount) padded.push('');
  return padded.slice(0, columnCount);
};

export const detectColumnCountFromTable = (table, showPercentageColumn = false) => {
  if (!table) return showPercentageColumn ? 3 : 2;

  const colgroupCols = table.querySelectorAll('colgroup col');
  if (colgroupCols.length > 0) return colgroupCols.length;

  const subRow = table.querySelector('thead .tr-header-sub-row');
  if (subRow) {
    return subRow.querySelectorAll('th').length + 1;
  }

  const headerRows = [...table.querySelectorAll('thead tr')].filter((tr) => !isReportHeaderTheadRow(tr));
  const currencyRow = headerRows.find(
    (tr) => tr.querySelector('td.td-header-sub-amount, td.td-header:not(.td-header-1)')
  );
  if (currencyRow) {
    const count = currencyRow.querySelectorAll('th, td').length;
    if (count > 0) return count;
  }

  const headerRow = table.querySelector('thead tr');
  if (headerRow && !isReportHeaderTheadRow(headerRow)) {
    const cells = expandHeaderCells(headerRow);
    if (cells.length > 0) return cells.length;
  }

  const firstDataRow = table.querySelector('tbody tr');
  if (firstDataRow) {
    const overflow = firstDataRow.querySelector('td.overflowed');
    if (overflow) {
      const colspan = parseInt(overflow.getAttribute('colspan') || '0', 10);
      if (colspan > 0) return colspan;
    }
    const sectionBlock = firstDataRow.querySelector('td.section-header-block');
    if (sectionBlock) {
      const colspan = parseInt(sectionBlock.getAttribute('colspan') || '0', 10);
      if (colspan > 0) return colspan;
    }
    const tdCount = firstDataRow.querySelectorAll('td').length;
    if (tdCount > 0) return tdCount;
  }

  return showPercentageColumn ? 3 : 2;
};

export const parseTheadRow = (tr, columnCount) => {
  if (!tr || isReportHeaderTheadRow(tr)) return null;
  const cells = expandHeaderCells(tr);
  if (!cells.length || cells.every((cell) => !cell)) return null;
  return { type: ROW_TYPES.COLUMN_HEADER, cells: padCells(cells, columnCount) };
};

export const parseTableColumnHeaders = (table) => {
  const thead = table?.querySelector('thead');
  if (!thead) return null;

  const subRow = thead.querySelector('.tr-header-sub-row');
  if (subRow) {
    const accountTh = thead.querySelector('.td-header-account');
    const cells = [normalizeHeaderText(accountTh?.textContent)];
    subRow.querySelectorAll('th').forEach((th) => {
      cells.push(normalizeHeaderText(th.textContent));
    });
    return { type: ROW_TYPES.COLUMN_HEADER, cells };
  }

  const headerRows = [...thead.querySelectorAll('tr')].filter((tr) => !isReportHeaderTheadRow(tr));
  const lastRow = headerRows[headerRows.length - 1];
  if (!lastRow) return null;

  const cells = expandHeaderCells(lastRow);
  return cells.length ? { type: ROW_TYPES.COLUMN_HEADER, cells } : null;
};

const getLabelCellText = (td) => {
  const label = td.querySelector('.pl-editable-account-label');
  const text = label?.textContent ?? td.textContent;
  return text?.replace(/\s+/g, ' ').trim() || '';
};

const isLabelCell = (td) =>
  td.classList.contains('td-account-cell') ||
  td.classList.contains('total-palcode') ||
  td.classList.contains('total-palcode-final');

const isNumericDataCell = (td) => {
  if (isLabelCell(td) || td.classList.contains('td-percentage') || td.classList.contains('overflowed')) {
    return false;
  }
  if (
    td.classList.contains('td-amount-cell') ||
    td.classList.contains('td-last') ||
    td.classList.contains('td-last-sum') ||
    td.classList.contains('td-last-sum-1')
  ) {
    return true;
  }
  const text = getLabelCellText(td);
  return Boolean(text);
};

export const parseTableRow = (tr, columnCount) => {
  const type = classifyTableRow(tr);
  if (!type) return null;

  const cells = Array.from({ length: columnCount }, () => '');

  if (type === ROW_TYPES.SECTION_MAJOR) {
    const label =
      tr.querySelector('.section-header-ag-label')?.textContent?.trim() ||
      tr.querySelector('.section-header-detail-label')?.textContent?.trim() ||
      tr.querySelector('.section-header-block')?.textContent?.trim() ||
      '';
    cells[0] = label;
    return { type, cells, mergeAll: true };
  }

  if (type === ROW_TYPES.PAL_CODE_HEADER) {
    const overflow = tr.querySelector('td.overflowed');
    cells[0] = overflow?.textContent?.trim() || '';
    return { type, cells, mergeAll: true };
  }

  let colIdx = 0;
  tr.querySelectorAll('td').forEach((td) => {
    if (colIdx >= columnCount) return;

    if (td.classList.contains('overflowed') || td.classList.contains('section-header-block')) return;

    if (isLabelCell(td)) {
      cells[colIdx] = getLabelCellText(td);
      colIdx += 1;
      return;
    }

    if (td.classList.contains('td-percentage')) {
      cells[colIdx] = getPercentageFromCell(td);
      colIdx += 1;
      return;
    }

    if (!isNumericDataCell(td)) {
      colIdx += 1;
      return;
    }

    cells[colIdx] = getAmountFromCell(td);
    colIdx += 1;
  });

  return { type, cells };
};

const parseReportHeaderRows = (doc) => {
  const rows = [];
  const header = doc.querySelector('.report-page-header');
  if (!header) return rows;

  const title = header.querySelector('.report-title')?.textContent?.trim();
  if (title) {
    rows.push({ type: ROW_TYPES.REPORT_TITLE, cells: [title], mergeAll: true });
  }

  header.querySelectorAll('.report-date-range').forEach((el) => {
    const text = el.textContent?.trim();
    if (text) rows.push({ type: ROW_TYPES.REPORT_META, cells: [text], mergeAll: true });
  });

  const clientName = header.querySelector('.report-client-name')?.textContent?.trim();
  if (clientName) rows.push({ type: ROW_TYPES.REPORT_META, cells: [clientName], mergeAll: true });

  const clientCase = header.querySelector('.report-client-case')?.textContent?.trim();
  if (clientCase) rows.push({ type: ROW_TYPES.REPORT_META, cells: [clientCase], mergeAll: true });

  const printDt = header.querySelector('.report-print-datetime')?.textContent?.trim();
  if (printDt) rows.push({ type: ROW_TYPES.REPORT_META, cells: [printDt], mergeAll: true });

  header.querySelectorAll('.palcodes-details').forEach((el) => {
    const text = el.textContent?.replace(/\s+/g, ' ').trim();
    if (text) rows.push({ type: ROW_TYPES.REPORT_META, cells: [text], mergeAll: true });
  });

  return rows;
};

const parseSectionHeaderRows = (container) => {
  const rows = [];

  container.querySelectorAll('.section-header-ag-label').forEach((el) => {
    const text = el.textContent?.trim();
    if (text) rows.push({ type: ROW_TYPES.SECTION_MAJOR, cells: [text], mergeAll: true });
  });

  container.querySelectorAll('.section-header-detail-label').forEach((el) => {
    const text = el.textContent?.trim();
    if (text) rows.push({ type: ROW_TYPES.SECTION_DETAIL, cells: [text], mergeAll: true });
  });

  return rows;
};

const processTable = (table, rows, columnCountRef, showPercentageColumn) => {
  const tableColumnCount = detectColumnCountFromTable(table, showPercentageColumn);
  columnCountRef.value = Math.max(columnCountRef.value, tableColumnCount);

  table.querySelectorAll('thead tr').forEach((tr) => {
    const headerRow = parseTheadRow(tr, tableColumnCount);
    if (headerRow) rows.push(headerRow);
  });

  table.querySelectorAll('tbody tr').forEach((tr) => {
    const row = parseTableRow(tr, tableColumnCount);
    if (row) rows.push(row);
  });
};

const processPageSection = (page, rows, columnCountRef, showPercentageColumn) => {
  [...page.children].forEach((child) => {
    if (child.classList?.contains('section-header-block')) {
      rows.push(...parseSectionHeaderRows(child));
      return;
    }

    if (child.tagName !== 'TABLE') return;

    processTable(child, rows, columnCountRef, showPercentageColumn);
  });
};

/**
 * Walk iframe report DOM in display order and build a typed row model for Excel export.
 */
export const parseReportDomToRows = (doc, { showPercentageColumn = false } = {}) => {
  const rows = [];
  const columnCountRef = { value: showPercentageColumn ? 3 : 2 };

  if (!doc) return { rows, columnCount: columnCountRef.value };

  rows.push(...parseReportHeaderRows(doc));

  const wrapper = doc.querySelector('.content-wrapper') || doc.body;
  wrapper.querySelectorAll('.pageStyle').forEach((page) => {
    if (page.classList.contains('report-page-header')) return;
    processPageSection(page, rows, columnCountRef, showPercentageColumn);
  });

  const organized = organizeExcelRows(rows);
  return { rows: organized, columnCount: columnCountRef.value };
};

const isCurrencyHeaderRow = (row) => {
  if (!row?.cells?.length) return false;
  return row.cells.some((cell) => {
    const text = String(cell || '');
    return text.includes('שקלים') || /shekel/i.test(text);
  });
};

const isPeriodHeaderRow = (row) => {
  if (!row?.cells?.length) return false;
  return row.cells.some((cell) => {
    const text = String(cell || '');
    return text.includes('לתקופה') || text.includes('סה״כ') || text.includes('סה"כ');
  });
};

/**
 * Collapse period + "שקלים חדשים" header pair into one organized row:
 * amount columns keep the period/total label; pct columns keep "%מהמכירות".
 */
export const combineHeaderRows = (periodRow, currencyRow) => {
  const period = periodRow?.cells || [];
  const currency = currencyRow?.cells || [];
  const len = Math.max(period.length, currency.length);
  const cells = [];
  let lastPeriod = '';

  for (let i = 0; i < len; i += 1) {
    const p = String(period[i] || '').trim();
    const c = String(currency[i] || '').trim();
    if (p) lastPeriod = p;

    if (c.includes('%')) {
      cells.push(c);
    } else if (p) {
      cells.push(p);
    } else if (c.includes('שקלים') || /shekel/i.test(c)) {
      cells.push(lastPeriod || '');
    } else {
      cells.push('');
    }
  }

  return { type: ROW_TYPES.COLUMN_HEADER, cells };
};

/** Strip repeated "שקלים חדשים" labels from a lone header row. */
const normalizeSingleHeaderRow = (row) => {
  if (!isCurrencyHeaderRow(row)) return row;
  return {
    ...row,
    cells: row.cells.map((cell) => {
      const text = String(cell || '').trim();
      if (text.includes('שקלים') || /shekel/i.test(text)) return '';
      return cell;
    }),
  };
};

/**
 * Keep a single organized column-header row (no per-page / per-section repeats).
 */
export const organizeExcelRows = (rows = []) => {
  const result = [];
  let headerEmitted = false;

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i];
    if (row.type !== ROW_TYPES.COLUMN_HEADER) {
      result.push(row);
      continue;
    }

    if (headerEmitted) continue;

    const next = rows[i + 1];
    if (
      next?.type === ROW_TYPES.COLUMN_HEADER &&
      ((isPeriodHeaderRow(row) && isCurrencyHeaderRow(next)) ||
        (isCurrencyHeaderRow(row) && isPeriodHeaderRow(next)))
    ) {
      const periodRow = isPeriodHeaderRow(row) ? row : next;
      const currencyRow = isCurrencyHeaderRow(row) ? row : next;
      result.push(combineHeaderRows(periodRow, currencyRow));
      i += 1;
    } else {
      result.push(normalizeSingleHeaderRow(row));
    }
    headerEmitted = true;
  }

  return result;
};

const isPercentageValue = (value) => typeof value === 'string' && value.includes('%');

const formatAccountingWidthSample = (num) => {
  const abs = Math.abs(num);
  const body = abs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num < 0 ? `(${body})` : body;
};

const isPercentageColumnIndex = (rows, colIdx) =>
  rows.some((row) => {
    if (row.type !== ROW_TYPES.COLUMN_HEADER) return false;
    return String(row.cells[colIdx] || '').includes('%');
  });

/** Size columns from header labels and cell values so large amounts do not render as #######. */
export const computeColumnWidths = (rows, columnCount) => {
  const pctColumns = new Set();
  for (let colIdx = 1; colIdx < columnCount; colIdx += 1) {
    if (isPercentageColumnIndex(rows, colIdx)) {
      pctColumns.add(colIdx);
    }
  }

  let accountWidth = 28;
  rows.forEach((row) => {
    const label = String(row.cells[0] || '');
    if (label) {
      accountWidth = Math.max(accountWidth, Math.min(label.length + 2, 48));
    }
  });

  return Array.from({ length: columnCount }, (_, colIdx) => {
    if (colIdx === 0) {
      return { wch: accountWidth };
    }

    const isPct = pctColumns.has(colIdx);
    let maxLen = isPct ? 10 : 16;

    rows.forEach((row) => {
      const val = row.cells[colIdx];
      if (typeof val === 'number' && !Number.isNaN(val)) {
        maxLen = Math.max(maxLen, formatAccountingWidthSample(val).length);
      } else if (typeof val === 'string' && val) {
        maxLen = Math.max(maxLen, val.length);
      }
    });

    const minW = isPct ? 10 : 16;
    const maxW = isPct ? 14 : 24;
    return { wch: Math.min(Math.max(maxLen + 1, minW), maxW) };
  });
};

const getRowLabelStyle = (type, accountStripeIndex = 0) => {
  switch (type) {
    case ROW_TYPES.REPORT_TITLE:
      return titleStyle;
    case ROW_TYPES.REPORT_META:
      return metaStyle;
    case ROW_TYPES.SECTION_MAJOR:
      return sectionMajorStyle;
    case ROW_TYPES.SECTION_DETAIL:
      return sectionDetailStyle;
    case ROW_TYPES.COLUMN_HEADER:
      return columnHeaderStyle;
    case ROW_TYPES.PAL_CODE_HEADER:
      return palCodeHeaderStyle;
    case ROW_TYPES.ACCOUNT:
      return accountStyle(accountStripeIndex % 2 === 1);
    case ROW_TYPES.PROFIT_INDICATOR:
      return withFill(
        { font: { bold: true, italic: true, sz: 11 }, alignment: ALIGN_RIGHT },
        COLORS.palSubtotalBg,
      );
    case ROW_TYPES.PAL_SUBTOTAL:
      return boldStyle(COLORS.palSubtotalBg);
    case ROW_TYPES.CATEGORY_TOTAL:
      return categoryTotalStyle;
    case ROW_TYPES.GROUP_TOTAL:
      return groupTotalStyle;
    case ROW_TYPES.RUNNING_PL_TOTAL:
      return boldStyle(COLORS.groupTotalBg);
    case ROW_TYPES.GRAND_TOTAL:
      return grandTotalStyle;
    default:
      return accountStyle(false);
  }
};

const getAmountStyleForRow = (type, accountStripeIndex = 0) => {
  switch (type) {
    case ROW_TYPES.CATEGORY_TOTAL:
      return categoryAmountStyle;
    case ROW_TYPES.GROUP_TOTAL:
    case ROW_TYPES.RUNNING_PL_TOTAL:
      return groupAmountStyle;
    case ROW_TYPES.GRAND_TOTAL:
      return grandAmountStyle;
    case ROW_TYPES.PAL_SUBTOTAL:
      return boldAmountStyle(COLORS.palSubtotalBg);
    case ROW_TYPES.PROFIT_INDICATOR:
      return boldAmountStyle(COLORS.palSubtotalBg);
    case ROW_TYPES.COLUMN_HEADER:
      return withFill(
        {
          font: { bold: true, sz: 12 },
          alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
          border: { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder },
        },
        COLORS.columnHeaderBg,
        COLORS.columnHeaderFg,
      );
    case ROW_TYPES.PAL_CODE_HEADER:
      return withFill(
        { font: { bold: true, sz: 11 }, alignment: ALIGN_LEFT, border: { bottom: thinBorder } },
        COLORS.palCodeBg,
      );
    case ROW_TYPES.SECTION_MAJOR:
      return withFill({ alignment: ALIGN_LEFT }, COLORS.sectionMajorBg);
    case ROW_TYPES.SECTION_DETAIL:
      return withFill({ alignment: ALIGN_LEFT }, COLORS.sectionDetailBg);
    case ROW_TYPES.REPORT_TITLE:
      return withFill({ alignment: ALIGN_LEFT }, COLORS.reportTitleBg);
    case ROW_TYPES.REPORT_META:
      return withFill({ alignment: ALIGN_LEFT }, COLORS.reportMetaBg);
    case ROW_TYPES.ACCOUNT: {
      const bg = accountStripeIndex % 2 === 1 ? COLORS.accountAltBg : COLORS.accountBg;
      return amountStyle(bg);
    }
    default:
      return amountStyle(COLORS.accountBg);
  }
};

const getRowHeight = (type) => {
  switch (type) {
    case ROW_TYPES.REPORT_TITLE:
      return 24;
    case ROW_TYPES.SECTION_MAJOR:
      return 20;
    case ROW_TYPES.COLUMN_HEADER:
    case ROW_TYPES.PAL_CODE_HEADER:
      return 18;
    case ROW_TYPES.GRAND_TOTAL:
      return 22;
    default:
      return 17;
  }
};

const buildCellObject = (value, style, { isNumeric = false } = {}) => {
  if (value === null || value === undefined || value === '') {
    return { v: '', t: 's', s: style };
  }

  if (isNumeric && typeof value === 'number' && !Number.isNaN(value)) {
    return { v: value, t: 'n', z: ACCOUNTING_NUM_FMT, s: style };
  }

  return { v: String(value), t: 's', s: style };
};

export const buildStyledWorksheet = (rows, columnCount) => {
  const ws = {};
  const merges = [];
  const rowHeights = [];
  let maxCol = 0;
  let accountStripeIndex = 0;

  rows.forEach((row, rowIdx) => {
    const cells = padCells(row.cells, columnCount);
    if (row.type === ROW_TYPES.ACCOUNT) {
      accountStripeIndex += 1;
    }
    const stripeIdx = row.type === ROW_TYPES.ACCOUNT ? accountStripeIndex - 1 : 0;
    const labelStyle = getRowLabelStyle(row.type, stripeIdx);
    const amountStyleForRow = getAmountStyleForRow(row.type, stripeIdx);
    rowHeights[rowIdx] = { hpt: getRowHeight(row.type) };

    cells.forEach((cellValue, colIdx) => {
      maxCol = Math.max(maxCol, colIdx);

      let style = colIdx === 0 ? labelStyle : amountStyleForRow;
      let isNumeric = false;

      if (row.mergeAll && colIdx > 0) return;

      if (colIdx > 0 && typeof cellValue === 'number' && !Number.isNaN(cellValue)) {
        isNumeric = true;
        style = amountStyleForRow;
      } else if (colIdx > 0 && isPercentageValue(cellValue)) {
        style = { ...amountStyleForRow, alignment: ALIGN_LEFT };
      } else if (row.type === ROW_TYPES.COLUMN_HEADER) {
        style = colIdx === 0 ? columnHeaderStyle : amountStyleForRow;
      }

      const addr = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
      ws[addr] = buildCellObject(cellValue, style, { isNumeric });
    });

    if (row.mergeAll && columnCount > 1) {
      merges.push({
        s: { r: rowIdx, c: 0 },
        e: { r: rowIdx, c: columnCount - 1 },
      });
    }
  });

  const totalRows = rows.length;
  const totalCols = Math.max(columnCount, maxCol + 1);
  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: Math.max(totalRows - 1, 0), c: Math.max(totalCols - 1, 0) },
  });
  ws['!merges'] = merges;
  ws['!rows'] = rowHeights;
  ws['!cols'] = computeColumnWidths(rows, totalCols);
  ws['!views'] = [{ rightToLeft: true, showGridLines: false }];

  return ws;
};

export const EXCEL_COLUMN_MODES = {
  ALL: 'all',
  MONTHS: 'months',
  TOTALS: 'totals',
};

const isTotalHeaderText = (text) => {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  if (!value) return false;
  return /סה["״]?כ/.test(value) || /^total$/i.test(value);
};

const isPercentageHeaderText = (text) => String(text || '').includes('%');

/**
 * Column indexes to keep for a monthly Excel download.
 * Totals mode keeps the account column plus the period-total amount (and its % column).
 */
export const getKeptColumnIndexes = (rows, columnCount, columnMode = EXCEL_COLUMN_MODES.ALL) => {
  const allIndexes = Array.from({ length: columnCount }, (_, i) => i);
  if (columnMode !== EXCEL_COLUMN_MODES.TOTALS || columnCount <= 1) {
    return allIndexes;
  }

  const header = rows.find((row) => row.type === ROW_TYPES.COLUMN_HEADER);
  const cells = header?.cells || [];
  const indexes = new Set([0]);

  let firstTotalIdx = -1;
  for (let i = 1; i < columnCount; i += 1) {
    if (isTotalHeaderText(cells[i])) {
      firstTotalIdx = i;
      break;
    }
  }

  if (firstTotalIdx >= 0) {
    for (let i = firstTotalIdx; i < columnCount; i += 1) {
      indexes.add(i);
    }
  } else {
    const lastIdx = columnCount - 1;
    if (lastIdx > 0 && isPercentageHeaderText(cells[lastIdx]) && lastIdx - 1 > 0) {
      indexes.add(lastIdx - 1);
      indexes.add(lastIdx);
    } else if (lastIdx > 0) {
      indexes.add(lastIdx);
    }
  }

  return [...indexes].sort((a, b) => a - b);
};

export const filterRowsToColumns = (rows = [], keptIndexes = []) =>
  rows.map((row) => ({
    ...row,
    cells: keptIndexes.map((idx) => (row.cells && row.cells[idx] !== undefined ? row.cells[idx] : '')),
  }));

export const applyExcelColumnMode = (rows, columnCount, columnMode = EXCEL_COLUMN_MODES.ALL) => {
  const keptIndexes = getKeptColumnIndexes(rows, columnCount, columnMode);
  return {
    rows: filterRowsToColumns(rows, keptIndexes),
    columnCount: keptIndexes.length,
  };
};

const getExcelFileNameSuffix = (columnMode) => {
  if (columnMode === EXCEL_COLUMN_MODES.TOTALS) return '_Totals';
  if (columnMode === EXCEL_COLUMN_MODES.MONTHS) return '_ByMonths';
  return '';
};

/**
 * Build a styled P&L workbook from the editable iframe DOM and trigger download.
 */
export const buildProfitAndLossExcelFromDocument = (
  doc,
  { reportData, showPercentageColumn = false, columnMode = EXCEL_COLUMN_MODES.ALL } = {},
) => {
  const parsed = parseReportDomToRows(doc, { showPercentageColumn });
  const { rows, columnCount } = applyExcelColumnMode(parsed.rows, parsed.columnCount, columnMode);

  if (!rows.length) {
    return { ok: false, reason: 'noData' };
  }

  const ws = buildStyledWorksheet(rows, columnCount);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'ProfitAndLoss');
  applyWorkbookRtl(wb);

  const fileName = `${reportData?.clientName || 'Client'}_ProfitAndLossReport${getExcelFileNameSuffix(columnMode)}.xlsx`;
  XLSX.writeFile(wb, fileName);

  return { ok: true, fileName };
};
